import React, { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  FormSection,
  IconButton,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import ReactMarkdown from "react-markdown";

import { DialogProps, DisplayTokenDialogProps } from "./types";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";

const useCopyText = () => {
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  return {
    copyText: async (text: string) => {
      await navigator.clipboard.writeText(text);
      sendNotification({
        type: "success",
        text: l10n.getString("notification-text-copied"),
      });
    },
  };
};

export const getAlertError = (message: string, onDismiss: () => void) => (
  <Alert isActive kind="error" isDismissable onUserDismiss={onDismiss}>
    {message}
  </Alert>
);

/**
 * Flattens DRF-style field errors (string or array-of-string values keyed by
 * field name) into a flat list of human-readable messages, skipping the
 * `id`/`message` envelope keys.
 */
const flattenFieldErrors = (data: Record<string, unknown>): string[] =>
  Object.entries(data).flatMap(([key, value]) => {
    if (key === "id" || key === "message") return [];
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) {
      return value.filter((v): v is string => typeof v === "string");
    }
    return [];
  });

/**
 * Normalizes an `ApiHttp.fetch` rejection into displayable messages. Handles
 * plain string rejections and DRF validation payloads such as
 * `{ scope: ["..."], id: "invalid_choice", message: { scope: [...] } }`.
 */
export const getApiErrorMessages = (error: unknown): string[] => {
  if (typeof error === "string") return [error];
  if (error && typeof error === "object") {
    const data = error as Record<string, unknown>;
    const source =
      data.message && typeof data.message === "object"
        ? (data.message as Record<string, unknown>)
        : data;
    return flattenFieldErrors(source);
  }
  return [];
};

export const DisplayOnceTokenDialog = ({
  isOpen,
  onUserDismiss,
  createdToken,
}: DisplayTokenDialogProps) => {
  if (!createdToken) {
    return null;
  }
  const { l10n } = useLocalization();
  const { copyText } = useCopyText();

  return (
    <Dialog
      title={l10n.getString("dialog-api-token-title")}
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      width="600px"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={() => copyText(createdToken.token)}>
            {l10n.getString("button-copy-token")}
          </Button>
        </div>
      }
    >
      <div style={{ fontSize: "var(--font-size-s)", margin: "10px 0" }}>
        <span>{l10n.getString("dialog-api-token-security-message")}</span>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          fontWeight: "bold",
          color: "var(--font-color-heading)",
        }}
      >
        <FormSection title={l10n.getString("form-section-bearer-token")} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "var(--bgColor-smokeGrey)",
            padding: "10px",
          }}
        >
          <span>{createdToken?.token}</span>
          <IconButton
            name="copy"
            onClick={() => copyText(createdToken?.token || "")}
          />
        </div>
      </div>
    </Dialog>
  );
};

export const TermsOfUseDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
}: DialogProps) => {
  const [hasAgreed, setHasAgreed] = useState(false);
  const [alertEl, setAlertEl] = useState<React.ReactNode>(null);
  const institutionSettings = useInstitutionSettings();
  const { l10n } = useLocalization();

  useEffect(() => {
    setAlertEl(null);
    setHasAgreed(false);
  }, [isOpen]);

  const onConfirmInner = () => {
    setAlertEl(null);
    if (hasAgreed) {
      onConfirm();
    } else {
      setAlertEl(
        getAlertError(l10n.getString("alert-must-accept-terms"), () =>
          setAlertEl(null)
        )
      );
    }
  };

  const dialogFooter = (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        style={{ paddingRight: "10px" }}
        kind="plain"
        onClick={onUserDismiss}
      >
        {l10n.getString("button-cancel")}
      </Button>
      <Button onClick={onConfirmInner}>
        {l10n.getString("button-confirm")}
      </Button>
    </div>
  );

  return (
    <Dialog
      title={l10n.getString("dialog-terms-title")}
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      notification={alertEl}
      footer={dialogFooter}
    >
      <div style={{ whiteSpace: "pre-wrap" }}>
        <ReactMarkdown>
          {institutionSettings.api_banking_agreement as string}
        </ReactMarkdown>
      </div>
      <br />
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <Checkbox
          label={l10n.getString("checkbox-accept-terms")}
          name="agree"
          checked={hasAgreed}
          onChange={() => setHasAgreed(!hasAgreed)}
        />
      </div>
    </Dialog>
  );
};
